import { highLevelApi } from '@sdk/main'
import type {
  QueryPayload,
  CRMQueryPayload,
  UpsertCRMObjectsPayload,
  CRMSmartObjectValidationRulesPayload,
  CRMSmartObjectMetadataPayload,
  CRMSmartObjectLayoutPayload,
  CRMSmartDeleteObjectsPayload,
  CRMCreatePayload,
  CRMUpsertPayload,
  CRMDescribePayload,
  CRMLayoutPayload,
} from '@sdk/payload.types'
import { convertSoqlToSmartQuery, findMainFromClause } from '@lib/util/smart-store.util'

export function query(payload: QueryPayload) {
  return highLevelApi.API.request('query', payload)
}

export function crmQuery(payload: CRMQueryPayload): Promise<any> {
  return highLevelApi.API.request('crm_query', payload)
}

/**
 * Extracts field names from the main SOQL query's SELECT clause (ignores subqueries).
 * Handles simple fields, relationship fields (Account.Name), and aliases.
 */
function extractFieldsFromSoql(soqlQuery: string): string[] {
  const { cleanQuery } = findMainFromClause(soqlQuery.replace(/\s+/g, ' ').trim())
  const selectMatch = cleanQuery.match(/SELECT\s+([\s\S]+?)\s+FROM\s+/i)
  if (!selectMatch) return []

  const selectClause = selectMatch[1]
  const fields: string[] = []
  let depth = 0
  let currentField = ''

  for (let i = 0; i < selectClause.length; i++) {
    const char = selectClause[i]

    if (char === '(') {
      depth++
      currentField += char
    } else if (char === ')') {
      depth--
      currentField += char
    } else if (char === ',' && depth === 0) {
      if (currentField.trim()) {
        fields.push(currentField.trim())
      }
      currentField = ''
    } else {
      currentField += char
    }
  }

  if (currentField.trim()) {
    fields.push(currentField.trim())
  }

  // Extract just the field name (handle aliases like "Name AS n" or relationship fields like "Account.Name")
  return fields.map((field) => {
    // Remove alias (AS keyword or space followed by identifier)
    const aliasMatch = field.match(/^(.+?)\s+(?:AS\s+)?([A-Za-z_][A-Za-z0-9_]*)$/i)
    const fieldExpr = aliasMatch ? aliasMatch[1].trim() : field.trim()

    // For relationship fields like "Account.Name", keep the last part as the field name,
    // but we'll use the full expression for matching
    return fieldExpr
  })
}

/**
 * Extracts the primary object name from the main SOQL query's FROM clause (ignores subqueries).
 */
function extractObjectFromSoql(soqlQuery: string): string {
  const { primaryObject } = findMainFromClause(soqlQuery.replace(/\s+/g, ' ').trim())
  return primaryObject ?? ''
}

/**
 * Sets a nested property on an object using a dot-separated path.
 * For example, setNestedProperty(obj, 'Child_Account_vod__r.Name', 'John')
 * results in { Child_Account_vod__r: { Name: 'John' } }
 */
function setNestedProperty(obj: Record<string, any>, path: string, value: any): void {
  const parts = path.split('.')

  if (parts.length === 1) {
    // Simple field - set directly
    obj[path] = value
    return
  }

  // Relationship field - create nested structure
  let current = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (!current[part] || typeof current[part] !== 'object') {
      current[part] = {}
    }
    current = current[part]
  }
  current[parts[parts.length - 1]] = value
}

/**
 * Checks if a field is a special "all fields" selector.
 * These include FIELDS(ALL), FIELDS(STANDARD), FIELDS(CUSTOM), and wildcard *.
 */
function isAllFieldsSelector(field: string): boolean {
  const trimmed = field.trim()
  return trimmed === '*' || /^FIELDS\s*\(\s*(ALL|STANDARD|CUSTOM)\s*\)$/i.test(trimmed)
}

/**
 * Transforms SmartStore query results (array of arrays) into CRM query format (array of objects).
 * Handles relationship fields by creating nested objects matching Salesforce REST API format.
 *
 * For example, field "Child_Account_vod__r.Name" with value "John" becomes:
 * { Child_Account_vod__r: { Name: "John" } }
 *
 * Special handling for FIELDS(ALL), FIELDS(STANDARD), FIELDS(CUSTOM), and * selectors:
 * When these are used, SmartStore returns the entire soup object, which is spread into the record
 * instead of being assigned to a single field key.
 *
 * @param smartStoreResults - Array of arrays from SmartStore query
 * @param fields - Field names from the original SOQL query
 * @param objectName - Primary object name from the SOQL query
 * @returns Array of objects matching CRM query result format
 */
function transformSmartStoreResults(smartStoreResults: any[][], fields: string[], objectName: string): any[] {
  if (!smartStoreResults || !Array.isArray(smartStoreResults)) {
    return []
  }

  return smartStoreResults.map((row) => {
    const record: Record<string, any> = {}

    // Map each value to its field name, creating nested objects for relationship fields
    fields.forEach((field, index) => {
      if (index < row.length) {
        const value = row[index]

        // Special handling for "all fields" selectors (FIELDS(ALL), FIELDS(STANDARD), FIELDS(CUSTOM), *)
        // SmartStore returns the entire soup entry as a single object for {ObjectName:_soup}
        // Spread its properties into the record instead of assigning to a single field key
        if (isAllFieldsSelector(field)) {
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Spread all properties from the soup object into the record
            Object.assign(record, value)
          }
        } else {
          // Regular field - set normally
          setNestedProperty(record, field, value)
        }
      }
    })

    // Add attributes object (similar to Salesforce REST API format)
    // Get Id from the record if it exists for the URL
    const recordId = record['Id'] || record['id'] || ''
    record['attributes'] = {
      type: objectName,
      url: recordId ? `/services/data/v57.0/sobjects/${objectName}/${recordId}` : '',
    }

    return record
  })
}

/**
 * Executes a CRM query with automatic iOS/SmartStore adaptation.
 *
 * On iOS devices with the 'sfdc_offline_enabled' LaunchDarkly flag enabled,
 * this function automatically converts SOQL queries to SmartStore Smart SQL format
 * and uses the local SmartStore for offline data access.
 * On other platforms or when the flag is disabled, it uses the standard CRM query API.
 *
 * @param {CRMQueryPayload} payload - The query payload containing the SOQL query string.
 * @returns {Promise<any>} - Promise resolving with query results.
 *
 * @example
 * // Works on all platforms - automatically adapts for iOS with sfdc_offline_enabled LD flag
 * const result = await crmQueryAdaptive({ query: 'SELECT Id, Name FROM Account WHERE Active = true' })
 */
export async function crmQueryAdaptive(payload: CRMQueryPayload): Promise<any> {
  const query = payload.query

  if (!query || typeof query !== 'string') {
    return Promise.reject(new Error('Query is required and must be a string'))
  }

  // Check if SmartStore should be used (iOS + sfdc_offline_enabled LD flag)
  let useSmartStore = false
  try {
    const env: any = await highLevelApi.API.request('get_env')
    const isIos = env?.mode === 'IOS'
    const isSfdcSyncEnabled = env?.launch_darkly?.sfdc_offline_enabled === true
    useSmartStore = isIos && isSfdcSyncEnabled
  } catch {
    useSmartStore = false
  }

  console.log(`[crmQueryAdaptive] Called query: `, query)
  if (useSmartStore) {
    // Extract field names and object name from the original SOQL query
    const fields = extractFieldsFromSoql(query)
    const objectName = extractObjectFromSoql(query)

    // Convert SOQL to SmartStore Smart SQL
    let smartQuery = convertSoqlToSmartQuery(query)

    // Add SmartStore filter to exclude locally deleted records (unless already in query).
    // SmartStore stores boolean flags as 0/1, 'false'/'true', or NULL.
    if (!smartQuery.includes('__locally_deleted__')) {
      const fromMatch = smartQuery.match(/FROM\s+\{([A-Za-z0-9_]+)\}/)
      const primaryObject = fromMatch?.[1]
      if (primaryObject) {
        const filter = `({${primaryObject}:__locally_deleted__} = 0 OR {${primaryObject}:__locally_deleted__} = 'false' OR {${primaryObject}:__locally_deleted__} IS NULL)`
        if (smartQuery.includes(' WHERE ')) {
          // Wrap existing conditions in parentheses so AND binds to the whole clause.
          // Otherwise "A OR B" + " AND filter" becomes A OR (B AND filter) by precedence.
          smartQuery = smartQuery.replace(
            /\s+WHERE\s+([\s\S]+?)(?=\s+ORDER\s+BY\s+|\s+GROUP\s+BY\s+|\s+HAVING\s+|\s+LIMIT\s+|\s+OFFSET\s+|\s*$)/i,
            (_, conditions) => ` WHERE (${conditions}) AND ${filter}`,
          )
        } else {
          // Insert WHERE after FROM {Object}, before ORDER BY / LIMIT / etc. (appending would produce invalid SQL)
          smartQuery = smartQuery.replace(
            /(FROM\s+\{[A-Za-z0-9_]+\})((\s+(?:ORDER\s+BY|GROUP\s+BY|HAVING|LIMIT|OFFSET)[\s\S]*)?)$/i,
            (_, fromClause, rest) => `${fromClause} WHERE ${filter}${rest}`,
          )
        }
      }
    }

    console.log(`[crmSmartQuery] Called query: `, smartQuery)

    // Execute SmartStore query
    const smartStoreResults = await crmSmartQuery({ query: smartQuery })

    // Transform results to match CRM query format
    const records = transformSmartStoreResults(smartStoreResults, fields, objectName)
    console.log(`[SmartStore] Results: `, records)

    return {
      records,
      totalSize: records.length,
    }
  }

  // Non-iOS or setting disabled: use standard CRM query
  return crmQuery(payload)
}

/**
 * Executes a SmartStore query against local CRM data (iOS only).
 * Uses Salesforce Mobile SDK SmartStore query syntax.
 * Validates that the query uses SmartStore syntax before execution.
 *
 * @param {CRMQueryPayload} payload - The query payload containing the SmartStore query string.
 * @returns {Promise<Array<any>>} - Promise resolving with an array of query results from SmartStore.
 * @throws {Error} - Throws an error if the query is not a valid SmartStore query.
 *
 * @example
 * // iOS only method to execute a SmartStore query.
 * api.crmSmartQuery({ query: 'SELECT {Account:Id}, {Account:Name} FROM {Account} ORDER BY {Account:Name} LIMIT 10' })
 */
export function crmSmartQuery(payload: CRMQueryPayload): Promise<Array<any>> {
  const query = payload.query

  // Validate SmartStore query syntax: must contain {soupName:fieldPath} pattern
  if (!query || typeof query !== 'string') {
    return Promise.reject(new Error('Query is required and must be a string'))
  }

  const smartStorePattern = /\{[^}]+:[^}]+}/
  if (!smartStorePattern.test(query.trim())) {
    return Promise.reject(
      new Error(
        'Invalid SmartStore query syntax. SmartStore queries must use curly braces syntax: {SoupName:FieldName}',
      ),
    )
  }

  return highLevelApi.API.request('crm_smart_query', payload)
}

/**
 * Retrieves metadata for a CRM smart object (iOS only).
 * Fetches object metadata from the Salesforce Mobile SDK cache.
 *
 * @param {CRMSmartObjectMetadataPayload} payload - The payload containing the object name.
 * @returns {Promise<any>} - Promise resolving with the object metadata.
 * @throws {Error} - Throws an error if the object name is not provided.
 *
 * @example
 * // iOS only method to retrieve CRM object metadata.
 * api.crmSmartObjectMetadata({ object: 'Account' })
 */
export function crmSmartObjectMetadata(payload: CRMSmartObjectMetadataPayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  const objectName = payload.object

  if (!objectName || typeof objectName !== 'string' || objectName.trim() === '') {
    return Promise.reject(new Error('Object name is required and must be a non-empty string'))
  }

  if (
    payload.record_type_id !== undefined &&
    (typeof payload.record_type_id !== 'string' || payload.record_type_id.trim() === '')
  ) {
    return Promise.reject(new Error('record_type_id must be a non-empty string if provided'))
  }

  return highLevelApi.API.request('crm_smart_object_metadata', payload)
}

/**
 * Upserts CRM objects into local CRM data (iOS only).
 * Uses Salesforce Mobile SDK SmartStore to insert or update records.
 * Each object in the payload specifies a table name, the objects to upsert, and an optional external ID path.
 *
 * **Important**: Always provide `external_id_path` when performing updates to ensure records are
 * matched correctly. Without it, the operation may create duplicate records instead of updating existing ones.
 *
 * **Note**: When creating new records, you must provide an explicit identifier field (e.g., `Id`)
 * in each record object passed from the frontend. The backend does not auto-generate IDs.
 *
 * @param {UpsertCRMObjectsPayload} payload - The upsert payload containing an array of UpsertCRMObject items.
 * @returns {Promise<any>} - Promise resolving with the result of the upsert operation.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // iOS only method to upsert CRM objects.
 *
 * // Updating existing records - always include external_id_path
 * api.crmSmartUpsertObjects({
 *   objects: [
 *     {
 *       table_name: 'Account',
 *       objects: [{ Id: '001xx000003DGbQAAW', Name: 'Acme Corp Updated' }],
 *       external_id_path: 'Id' // Required for updates to match existing records
 *     }
 *   ]
 * })
 *
 * // Creating new records - explicit ID required in each record
 * api.crmSmartUpsertObjects({
 *   objects: [
 *     {
 *       table_name: 'Account',
 *       objects: [
 *         { Id: '001xx000003NEW001', Name: 'New Company' }, // Explicit ID required
 *         { Id: '001xx000003NEW002', Name: 'Another Company' }
 *       ]
 *     }
 *   ]
 * })
 */
export function crmSmartUpsertObjects(payload: UpsertCRMObjectsPayload): Promise<any> {
  if (!payload || !payload.objects || !Array.isArray(payload.objects)) {
    return Promise.reject(new Error('Payload must contain an array of objects'))
  }

  if (payload.objects.length === 0) {
    return Promise.reject(new Error('Objects array cannot be empty'))
  }

  for (const obj of payload.objects) {
    if (!obj) {
      return Promise.reject(new Error('Objects array cannot contain null or undefined elements'))
    }

    if (!obj.table_name || typeof obj.table_name !== 'string' || obj.table_name.trim() === '') {
      return Promise.reject(new Error('Each object must have a valid non-empty table_name'))
    }

    // Validate external_id_path if provided
    if (obj.external_id_path !== undefined) {
      if (typeof obj.external_id_path !== 'string' || obj.external_id_path.trim() === '') {
        return Promise.reject(
          new Error(
            `Object for table ${obj.table_name} has invalid external_id_path. It must be a non-empty string or undefined.`,
          ),
        )
      }
    }

    if (!obj.objects || !Array.isArray(obj.objects) || obj.objects.length === 0) {
      return Promise.reject(new Error(`Object for table ${obj.table_name} must have a non-empty objects array`))
    }

    // Validate that inner objects array doesn't contain null/undefined elements or arrays
    for (let i = 0; i < obj.objects.length; i++) {
      const record = obj.objects[i]
      if (!record || typeof record !== 'object' || Array.isArray(record)) {
        return Promise.reject(
          new Error(
            `Object for table ${obj.table_name} contains invalid element at index ${i}. All records must be valid objects, not null, undefined, or arrays.`,
          ),
        )
      }
    }
  }

  console.log('[crmSmartUpsertObjects] Request payload:', JSON.stringify(payload, null, 2))

  return highLevelApi.API.request('crm_smart_upsert_objects', payload)
    .then((result) => {
      console.log('[crmSmartUpsertObjects] Success:', result)
      return result
    })
    .catch((error) => {
      console.error('[crmSmartUpsertObjects] Error:', error)
      throw error
    })
}
/**
 * Retrieves validation rules for a CRM smart object (iOS only).
 * This method fetches the validation rules configured for a specific CRM object type
 * from the local SmartStore cache.
 *
 * @param {CRMSmartObjectValidationRulesPayload} payload - The payload containing the object name.
 * @returns {Promise<any>} - Promise resolving with the validation rules for the specified object.
 *
 * @example
 * // iOS only method to get validation rules for an Account object.
 * api.crmSmartObjectValidationRules({ object: 'Account' })
 *
 * @example
 * // Get validation rules for a Contact object.
 * api.crmSmartObjectValidationRules({ object: 'Contact' })
 */
export function crmSmartObjectValidationRules(payload: CRMSmartObjectValidationRulesPayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  const objectName = payload.object

  if (!objectName || typeof objectName !== 'string' || objectName.trim() === '') {
    return Promise.reject(new Error('Object name is required and must be a non-empty string'))
  }

  return highLevelApi.API.request('crm_smart_object_validation_rules', payload)
}

/**
 * Retrieves layout information for a CRM smart object (iOS only).
 * Fetches object layout metadata from the Salesforce Mobile SDK cache.
 *
 * @param {CRMSmartObjectLayoutPayload} payload - The payload containing the object name and optional layout parameters.
 * @returns {Promise<any>} - Promise resolving with the object layout metadata.
 * @throws {Error} - Throws an error if the object name is not provided.
 *
 * @example
 * // iOS only method to retrieve CRM object layout.
 * api.crmSmartObjectLayout({ object: 'Account' })
 *
 * @example
 * // Get layout with specific form factor and mode.
 * api.crmSmartObjectLayout({ object: 'Account', form_factor: 'Large', mode: 'Edit' })
 */
export function crmSmartObjectLayout(payload: CRMSmartObjectLayoutPayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  const objectName = payload.object

  if (!objectName || typeof objectName !== 'string' || objectName.trim() === '') {
    return Promise.reject(new Error('Object name is required and must be a non-empty string'))
  }

  return highLevelApi.API.request('crm_smart_object_layout', payload)
}

/**
 * Deletes CRM objects from local CRM data (iOS only).
 * Uses Salesforce Mobile SDK SmartStore to delete records.
 * Each object in the payload specifies a table name and an array of IDs to delete.
 *
 * @param {CRMSmartDeleteObjectsPayload} payload - The delete payload containing an array of CRMDeleteObject items.
 * @returns {Promise<any>} - Promise resolving with the result of the delete operation.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // iOS only method to delete CRM objects.
 * api.crmSmartDeleteObjects({
 *   objects: [
 *     {
 *       table_name: 'Account',
 *       ids: ['001xx000003DGbQAAW', '001xx000003DGbRABW']
 *     },
 *     {
 *       table_name: 'Contact',
 *       ids: ['003xx000004TmiQAAS']
 *     }
 *   ]
 * })
 */
export function crmSmartDeleteObjects(payload: CRMSmartDeleteObjectsPayload): Promise<any> {
  if (!payload || !payload.objects || !Array.isArray(payload.objects)) {
    return Promise.reject(new Error('Payload must contain an array of objects'))
  }

  if (payload.objects.length === 0) {
    return Promise.reject(new Error('Objects array cannot be empty'))
  }

  for (const obj of payload.objects) {
    if (!obj) {
      return Promise.reject(new Error('Objects array cannot contain null or undefined elements'))
    }

    if (!obj.table_name || typeof obj.table_name !== 'string' || obj.table_name.trim() === '') {
      return Promise.reject(new Error('Each object must have a valid non-empty table_name'))
    }

    if (!obj.ids || !Array.isArray(obj.ids) || obj.ids.length === 0) {
      return Promise.reject(new Error(`Object for table ${obj.table_name} must have a non-empty ids array`))
    }

    // Validate that ids array contains only non-empty strings
    for (let i = 0; i < obj.ids.length; i++) {
      const id = obj.ids[i]
      if (!id || typeof id !== 'string' || id.trim() === '') {
        return Promise.reject(
          new Error(
            `Object for table ${obj.table_name} contains invalid id at index ${i}. All IDs must be non-empty strings.`,
          ),
        )
      }
    }
  }

  console.log('[crmSmartDeleteObjects] Request payload:', JSON.stringify(payload, null, 2))

  return highLevelApi.API.request('crm_smart_delete_objects', payload)
    .then((result) => {
      console.log('[crmSmartDeleteObjects] Success:', result)
      return result
    })
    .catch((error) => {
      console.error('[crmSmartDeleteObjects] Error:', error)
      throw error
    })
}

/**
 * Creates new records in CRM (Web only).
 * Uses Salesforce REST API to create records.
 *
 * @param {CRMCreatePayload} payload - The create payload containing sobject type and records.
 * @returns {Promise<any>} - Promise resolving with the result of the create operation.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // Web only method to create CRM records.
 * api.crmCreate({
 *   sobject: 'Order__c',
 *   records: [
 *     { Account__c: '001xx000003DGbQAAW', Order_Date__c: '2024-01-15' },
 *     { Account__c: '001xx000003DGbRABW', Order_Date__c: '2024-01-16' }
 *   ]
 * })
 */
export function crmCreate(payload: CRMCreatePayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  if (!payload.sobject || typeof payload.sobject !== 'string' || payload.sobject.trim() === '') {
    return Promise.reject(new Error('sobject is required and must be a non-empty string'))
  }

  if (!payload.records || !Array.isArray(payload.records) || payload.records.length === 0) {
    return Promise.reject(new Error('records is required and must be a non-empty array'))
  }

  for (let i = 0; i < payload.records.length; i++) {
    const record = payload.records[i]
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return Promise.reject(
        new Error(`records contains invalid element at index ${i}. All records must be valid objects.`),
      )
    }
  }

  return highLevelApi.API.request('crm_create', payload)
}

/**
 * Upserts records in CRM (Web only).
 * Uses Salesforce REST API to insert or update records based on external ID.
 * If a record with matching external ID exists, it will be updated; otherwise, a new record is created.
 *
 * @param {CRMUpsertPayload} payload - The upsert payload containing sobject type, records, and external ID field.
 * @returns {Promise<any>} - Promise resolving with the result of the upsert operation.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // Web only method to upsert CRM records.
 * api.crmUpsert({
 *   sobject: 'Order__c',
 *   records: [
 *     { External_Id__c: 'ORD-001', Account__c: '001xx000003DGbQAAW', Status__c: 'Submitted' },
 *     { External_Id__c: 'ORD-002', Account__c: '001xx000003DGbRABW', Status__c: 'Draft' }
 *   ],
 *   external_id_field: 'External_Id__c'
 * })
 */
export function crmUpsert(payload: CRMUpsertPayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  if (!payload.sobject || typeof payload.sobject !== 'string' || payload.sobject.trim() === '') {
    return Promise.reject(new Error('sobject is required and must be a non-empty string'))
  }

  if (!payload.records || !Array.isArray(payload.records) || payload.records.length === 0) {
    return Promise.reject(new Error('records is required and must be a non-empty array'))
  }

  if (
    !payload.external_id_field ||
    typeof payload.external_id_field !== 'string' ||
    payload.external_id_field.trim() === ''
  ) {
    return Promise.reject(new Error('external_id_field is required and must be a non-empty string'))
  }

  for (let i = 0; i < payload.records.length; i++) {
    const record = payload.records[i]
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return Promise.reject(
        new Error(`records contains invalid element at index ${i}. All records must be valid objects.`),
      )
    }
  }

  return highLevelApi.API.request('crm_upsert', payload)
}

/**
 * Retrieves metadata/describe for a CRM object (Web only).
 * Uses Salesforce REST API to fetch object metadata including fields and picklist values.
 *
 * @param {CRMDescribePayload} payload - The describe payload containing the sobject name.
 * @returns {Promise<any>} - Promise resolving with the object metadata including fields and picklist values.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // Web only method to get CRM object metadata.
 * api.crmDescribe({ sobject: 'Account' })
 *   .then(metadata => {
 *     // Access fields
 *     console.log(metadata.fields)
 *     // Access picklist values for a specific field
 *     const industryField = metadata.fields.find(f => f.name === 'Industry')
 *     console.log(industryField.picklistValues)
 *   })
 */
export function crmDescribe(payload: CRMDescribePayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  if (!payload.sobject || typeof payload.sobject !== 'string' || payload.sobject.trim() === '') {
    return Promise.reject(new Error('sobject is required and must be a non-empty string'))
  }

  return highLevelApi.API.request('crm_describe', payload)
}

/**
 * Retrieves layout information for a CRM object (Web only).
 * Uses Salesforce REST API to fetch object layout including sections, fields arrangement, and form factors.
 *
 * @param {CRMLayoutPayload} payload - The layout payload containing the sobject name and optional layout parameters.
 * @returns {Promise<any>} - Promise resolving with the object layout information.
 * @throws {Error} - Throws an error if the payload is invalid.
 *
 * @example
 * // Web only method to get CRM object layout.
 * api.crmLayout({ sobject: 'Account' })
 *   .then(layout => {
 *     // Access layout sections
 *     console.log(layout.editLayoutSections)
 *   })
 *
 * @example
 * // Get layout with specific form factor, mode, and record type.
 * api.crmLayout({
 *   sobject: 'Account',
 *   form_factor: 'Large',
 *   mode: 'Edit',
 *   record_type_id: '012xx0000004ABC'
 * })
 */
export function crmLayout(payload: CRMLayoutPayload): Promise<any> {
  if (!payload) {
    return Promise.reject(new Error('Payload is required'))
  }

  if (!payload.sobject || typeof payload.sobject !== 'string' || payload.sobject.trim() === '') {
    return Promise.reject(new Error('sobject is required and must be a non-empty string'))
  }

  return highLevelApi.API.request('crm_layout', payload)
}
